/**
 * Email normalization utilities for handling case-insensitive email operations.
 *
 * According to RFC 5321, the local part of email addresses can be case-sensitive,
 * but in practice, most email providers treat them as case-insensitive.
 * For user experience and consistency, we normalize all emails to lowercase.
 */

/**
 * Normalizes an email address to lowercase and trims whitespace.
 * This should be used for all email comparisons, storage, and API calls.
 *
 * @param email - The email address to normalize
 * @returns The normalized email address (lowercase, trimmed)
 *
 * @example
 * normalizeEmail("User@Example.com  ") // returns "user@example.com"
 * normalizeEmail("ADMIN@TEST.ORG") // returns "admin@test.org"
 */
export const normalizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

/**
 * Normalizes an email for display purposes (trims whitespace but preserves case).
 * This can be used when you want to show the email to users while maintaining
 * some of the original formatting.
 *
 * @param email - The email address to normalize for display
 * @returns The display-normalized email address (trimmed, case preserved)
 *
 * @example
 * normalizeEmailForDisplay("User@Example.com  ") // returns "User@Example.com"
 */
export const normalizeEmailForDisplay = (email: string): string => {
  return email.trim();
};

/**
 * Compares two email addresses in a case-insensitive manner.
 *
 * @param email1 - First email address
 * @param email2 - Second email address
 * @returns true if emails are equivalent (case-insensitive), false otherwise
 *
 * @example
 * emailsEqual("User@Example.com", "user@EXAMPLE.com") // returns true
 */
export const emailsEqual = (email1: string, email2: string): boolean => {
  return normalizeEmail(email1) === normalizeEmail(email2);
};
